import React from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Link,
  Tooltip,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { useRefundDetails } from "src/hooks/useRefundDetails.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { Icon } from "@iconify/react";
import arrowLeftFill from "@iconify/icons-eva/arrow-left-fill";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import checkmarkCircle2Fill from "@iconify/icons-eva/checkmark-circle-2-fill";
import closeCircleFill from "@iconify/icons-eva/close-circle-fill";
import clockFill from "@iconify/icons-eva/clock-fill";
import copyFill from "@iconify/icons-eva/copy-fill";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { convertUTCToLocal } from "src/utils/dateTimeUtils";
import { format } from "date-fns";

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  minHeight: "400px",
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
}));

const getStatusColor = (status) => {
  switch (status) {
    case "captured":
    case "processed":
      return "success";
    case "refunded":
    case "failed":
      return "error";
    case "pending":
      return "warning";
    default:
      return "default";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "captured":
    case "processed":
      return checkmarkCircle2Fill;
    case "refunded":
    case "failed":
      return closeCircleFill;
    case "pending":
      return clockFill;
    default:
      return clockFill;
  }
};

const formatAmount = (amount) => {
  if (!amount) return "₹0.00";
  const amountInRupees = Number(amount) / 100;
  return `₹${amountInRupees.toFixed(2)}`;
};

const formatPaymentId = (paymentId) => {
  if (!paymentId) return "-";
  if (paymentId.length > 20) {
    return `...${paymentId.substring(paymentId.length - 10)}`;
  }
  return paymentId;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "-";
  
  // Handle different timestamp formats from API - all are assumed to be in UTC
  let utcValue;
  
  // Check if it's a Unix timestamp (number in seconds)
  if (typeof timestamp === 'number') {
    // Convert Unix timestamp (seconds) to ISO string (UTC)
    // Create Date from UTC timestamp, then convert to ISO string
    utcValue = new Date(timestamp * 1000).toISOString();
  } 
  // Check if it's already an ISO string or space-separated format
  else if (typeof timestamp === 'string') {
    const trimmed = timestamp.trim();
    
    // If it's in space-separated format like "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD HH:mm:ss.ssssss"
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(trimmed)) {
      // Replace space with T and add Z to explicitly mark as UTC
      utcValue = trimmed.replace(' ', 'T').split('.')[0] + 'Z';
    }
    // If it's already in ISO format with T but missing timezone, add Z to indicate UTC
    else if (trimmed.includes('T') && !trimmed.endsWith('Z') && !trimmed.match(/[+-]\d{2}:\d{2}$/)) {
      // Remove milliseconds if present and add Z
      utcValue = trimmed.split('.')[0] + 'Z';
    }
    // If it's just a date without time, add time and Z
    else if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      utcValue = trimmed + 'T00:00:00Z';
    }
    // If it already has Z or timezone offset, use as-is
    else if (trimmed.endsWith('Z') || trimmed.match(/[+-]\d{2}:\d{2}$/)) {
      utcValue = trimmed;
    }
    // Fallback: try to parse and convert to ISO
    else {
      const date = new Date(trimmed);
      if (isNaN(date.getTime())) return "-";
      utcValue = date.toISOString();
    }
  }
  // Fallback: try to parse as date
  else {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "-";
    utcValue = date.toISOString();
  }
  
  // Convert UTC to local time using convertUTCToLocal
  // This function properly handles UTC strings and converts them to local Date objects
  const localDate = convertUTCToLocal(utcValue);
  
  if (!localDate) return "-";
  
  // Format the local date using date-fns format
  // The localDate is already in local timezone, so format will display local time
  return format(localDate, "yyyy-MM-dd hh:mm:ss a");
};

export const RefundDetails = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const { teamId } = useSelectedTeam();
  const { data, isFetching, error } = useRefundDetails({ teamId, paymentId });
  const [copySnackbar, setCopySnackbar] = React.useState({
    open: false,
    message: "",
  });

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCopyRefundId = (refundId) => {
    navigator.clipboard
      .writeText(refundId)
      .then(() => {
        setCopySnackbar({
          open: true,
          message: "Refund ID copied to clipboard!",
        });
      })
      .catch(() => {
        setCopySnackbar({
          open: true,
          message: "Unable to copy refund ID. Please try again.",
        });
      });
  };

  const handleCloseSnackbar = () => {
    setCopySnackbar({ open: false, message: "" });
  };

  const handleCopyPaymentId = (paymentId) => {
    navigator.clipboard
      .writeText(paymentId)
      .then(() => {
        setCopySnackbar({
          open: true,
          message: "Payment ID copied to clipboard!",
        });
      })
      .catch(() => {
        setCopySnackbar({
          open: true,
          message: "Unable to copy payment ID. Please try again.",
        });
      });
  };

  if (isFetching) {
    return (
      <LoadingContainer>
        <Icon
          icon={refreshFill}
          width={48}
          height={48}
          style={{
            color: "#1976d2",
            marginBottom: 16,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
            mb: 1,
          }}
        >
          Loading Payment Details...
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            textAlign: "center",
            maxWidth: 300,
          }}
        >
          Loading payment details for {paymentId}...
        </Typography>
        <CircularProgress
          size={24}
          sx={{
            mt: 2,
            color: "#1976d2",
          }}
        />
      </LoadingContainer>
    );
  }

  if (error) {
    const is422Error = error?.response?.status === 422;
    const is404Error = error?.response?.status === 404;
    const is500Error = error?.response?.status === 500;

    let errorMessage = "We're having trouble loading the payment details";
    let errorTitle = "Unable to Load Payment Information";

    if (is422Error) {
      errorMessage =
        "The payment ID format is incorrect. It should start with 'pay_' and be 10-50 characters long.";
      errorTitle = "Invalid Payment ID";
    } else if (is404Error) {
      errorMessage = `We couldn't find any refund details for payment ID: ${paymentId}. Please check if the payment ID is correct.`;
      errorTitle = "Payment Not Found";
    } else if (is500Error) {
      errorMessage =
        "Our servers are experiencing issues. Please try again in a few moments.";
      errorTitle = "Server Error";
    } else {
      errorMessage = error.message || errorMessage;
    }

    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={handleGoBack} color="primary">
            <Icon icon={arrowLeftFill} width={24} height={24} />
          </IconButton>
          <Typography variant="h4"></Typography>
        </Stack>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {errorTitle}
          </Typography>
          <Typography variant="body2">{errorMessage}</Typography>
        </Alert>
        <Alert severity="info">
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Need help?</strong> Here are some details that might help
            our support team:
          </Typography>
          <Typography variant="body2">
            <strong>Payment ID:</strong> {paymentId}
          </Typography>
          <Typography variant="body2">
            <strong>Team ID:</strong> {teamId}
          </Typography>
          <Typography variant="body2">
            <strong>Error Code:</strong> {error?.response?.status || "Unknown"}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={handleGoBack} color="primary">
            <Icon icon={arrowLeftFill} width={24} height={24} />
          </IconButton>
          <Typography variant="h4"></Typography>
        </Stack>
        <Alert severity="warning">
          No refund details found for payment {paymentId}
        </Alert>
      </Box>
    );
  }

  const { paymentDetails, refunds, refundSummary, message } = data;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={handleGoBack} color="primary">
          <Icon icon={arrowLeftFill} width={24} height={24} />
        </IconButton>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #4A90E2 0%, #7DD3FC 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 0.5,
            }}
          >
            Payment Details
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={paymentId || ""} placement="top">
              <Typography variant="body2" color="text.secondary" sx={{ cursor: 'help' }}>
                Payment ID: {paymentId || '-'}
              </Typography>
            </Tooltip>
            {paymentId && paymentId.length > 20 && (
              <Tooltip title="Copy full Payment ID">
                <IconButton
                  size="small"
                  onClick={() => handleCopyPaymentId(paymentId)}
                  sx={{
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Icon icon={copyFill} width={14} height={14} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Stack>

      {/* Payment Details */}
      <StyledCard sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Payment Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Payment ID
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip
                title={
                  paymentDetails?.id && paymentDetails.id.length > 20
                    ? paymentDetails.id
                    : ""
                }
                placement="top"
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    cursor:
                      paymentDetails?.id && paymentDetails.id.length > 20
                        ? "help"
                        : "default",
                  }}
                >
                  {formatPaymentId(paymentDetails?.id)}
                </Typography>
              </Tooltip>
              {paymentDetails?.id && paymentDetails.id.length > 20 && (
                <Tooltip title="Copy full Payment ID">
                  <IconButton
                    size="small"
                    onClick={() => handleCopyPaymentId(paymentDetails.id)}
                    sx={{
                      p: 0.5,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Icon icon={copyFill} width={14} height={14} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Amount
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatAmount(paymentDetails?.amount)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Currency
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {paymentDetails?.currency || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Chip
              label={paymentDetails?.status || "-"}
              color={getStatusColor(paymentDetails?.status)}
              icon={
                <Icon
                  icon={getStatusIcon(paymentDetails?.status)}
                  width={16}
                  height={16}
                />
              }
              sx={{ textTransform: "capitalize" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Payment Method
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, textTransform: "uppercase" }}
            >
              {paymentDetails?.method || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              VPA
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {paymentDetails?.vpa || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {paymentDetails?.email || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Contact
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {paymentDetails?.contact || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Created At
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatTimestamp(paymentDetails?.createdAt)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Captured At
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatTimestamp(paymentDetails?.createdAt)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Typography variant="body2" color="text.secondary">
              Description
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {paymentDetails?.description || "-"}
            </Typography>
          </Grid>
        </Grid>
      </StyledCard>

      {/* Empty Refunds Message */}
      {(!refunds || refunds.length === 0) && message && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            fontStyle: "italic",
            mt: 1,
            mb: 2,
            fontSize: "0.75rem",
            pl: 1,
          }}
        >
          {message}
        </Typography>
      )}

      {/* Refunds Table */}
      {refunds && refunds.length > 0 && (
        <StyledCard>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Refund History
          </Typography>
          <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Refund ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>UTR / RRN</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refunds.map((refund, index) => (
                  <TableRow key={refund.refundId || index}>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {refund.refundId || "-"}
                        </Typography>
                        {refund.refundId && (
                          <Tooltip title="Copy Refund ID">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopyRefundId(refund.refundId)
                              }
                              sx={{ p: 0.5 }}
                            >
                              <Icon icon={copyFill} width={16} height={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {refund.refundId && paymentDetails?.notes?.gatewayName !== 'Paytm' && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          To track live status on Razorpay, follow this link:{' '}
                          <Link
                            href={
                              paymentDetails?.notes?.gatewayName === "Paytm"
                                ? "https://paytm.com/care"
                                : "https://razorpay.com/support/#refund"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: "underline" }}
                          >
                            {paymentDetails?.notes?.gatewayName === "Paytm"
                              ? "https://paytm.com/care"
                              : "https://razorpay.com/support/#refund"}
                          </Link>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "error.main" }}
                      >
                        {formatAmount(refund.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={refund.status || "-"}
                        color={getStatusColor(refund.status)}
                        icon={
                          <Icon
                            icon={getStatusIcon(refund.status)}
                            width={16}
                            height={16}
                          />
                        }
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {refund?.acquirerData?.rrn || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTimestamp(refund.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </StyledCard>
      )}

      {/* Copy Snackbar */}
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={copySnackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};
